import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckboxVisibility, DayOfWeek, Icon, TooltipDelay, TooltipHost } from "office-ui-fabric-react";
import { PersistTimelineList } from "../common/extensibleEntity/EntityTimelineList";
import { OnItemRender, TInlineEditProps } from "../common/extensibleEntity/EntityDetailsList";
import { IScaleTimelineSegment } from "../common/timeline/TimelineSegment";
import { IRow } from "../common/timeline/TimelineList";
import { ScaleRenderMode } from "../common/timeline/TimelineBody";
import { TimelineScale } from "../common/timeline/TimelineScale";
import { getWorkingHoursBetweenDates } from "../common/timeline/utils";
import { ClearObject, formatValue } from "../utils/common";
import { Dictionary, EntityType, IExtensibleEntity, ITimeframe, Quantization } from "../../entities/common";
import { Field, FormatType } from "../../entities/Metadata";
import { CalendarDataSet } from "../../store/calendar/common";
import {
    TimeTrackingEntry,
    TimeTrackingEntryAttr,
    TimeTrackingLine,
    TimeTrackingEntrySourceType,
    TimeTrackingSuggestionType,
    TimeTrackingEntrySourceInfo,
    CreateTimeTrackingEntryAttr,
    TimeTrackingProjectDetails
} from "../../store/TimeTrackingStore";
import SelectionExt from "../common/SelectionExt";
import TimeTrackingCell, { TimeTrackingCellValueType } from "./cells/TimeTrackingCell";
import TotalTimeTrackingCell, { getTimeTrackinLines } from "./cells/TotalTimeTrackingCell";
import TimeTrackingLineEntityName from "./TimeTrackingLineEntityName";
import TimeTrackingItemMenu from "./TimeTrackingItemMenu";
import TimeTrackingCellTooltip from "./tooltips/cell/TimeTrackingCellTooltip";
import ApprovalStatusTooltip from "./tooltips/ApprovalStatusTooltip";
import {
    ControlSettings,
    NameField,
    TotalReportedField,
    ApprovalStatusField,
    BuildLineGroup,
    TimeType,
    IsTimesheetLineReadonly,
    TimeTrackingReadonly,
    renderApprovalStatusIcon
} from "./common";
import { IMaybeTotal, WithEntityType } from "./TimeTrackingGrid";
import { nameof } from "../../store/services/metadataService";

interface TimeTrackingGridCoreProps {
    lines: TimeTrackingLine[];
    projectLines: TimeTrackingLine[];
    timeFrame: ITimeframe;
    isReadonly: boolean;
    isPeriodOpenForReporting: boolean;
    isLoading: boolean;
    resourceCalendarDataSet?: CalendarDataSet;
    controlSettings: ControlSettings;
    firstDayOfWeek: DayOfWeek;
    showApprovalColumn?: boolean;
    showTotalReportedColumn?: boolean;
    showLineMenu?: boolean;
    showCellTooltip?: boolean;
    nameColumnLabel?: string;
    additionalFields?: Field[];
    onAdditionalFieldRender?: OnItemRender<TimeTrackingLine & IMaybeTotal & WithEntityType>;
    onNameSuffixRender?: (line: TimeTrackingLine & IMaybeTotal & WithEntityType) => JSX.Element | undefined;
    inlineEditProps?: TInlineEditProps<TimeTrackingLine>;
    isLineReadonly?: (line: TimeTrackingLine) => boolean;
    isLineLocked?: (line: TimeTrackingLine) => boolean;
    cellValueTypes?: TimeTrackingCellValueType[];
    getCellValue?: (line: TimeTrackingLine, segment: IScaleTimelineSegment, typeKey: string) => number | undefined;
    onCellEditCompleted?: (line: TimeTrackingLine, segment: IScaleTimelineSegment, typeKey: string, value: number) => void;

    onEditEntry?: (entry: TimeTrackingEntry, timeType: TimeType) => void;
    onAddTime?: (project: TimeTrackingProjectDetails, timeType: TimeType) => void;
    onCreateEntry?: (segment: IScaleTimelineSegment, timeTrackingLine: TimeTrackingLine) => void;
    onDeleteEntries?: (ids: string[]) => void;
    onUpdateEntries?: (updates: { id: string; attributes: Partial<TimeTrackingEntryAttr> }[]) => void;
    onAddTimeEntries?: (
        entries: { attributes: CreateTimeTrackingEntryAttr; sourceInfo?: TimeTrackingEntrySourceInfo }[]
    ) => void;
    onClearTimeEntries?: (entries: TimeTrackingEntry[]) => void;
    onCopyTask?: (entries: CreateTimeTrackingEntryAttr[]) => void;
    onCloneEntry?: (entries: { attributes: CreateTimeTrackingEntryAttr }[]) => void;
    onRecallLine?: (lineId: string, projectName: string, isAdministrative: boolean) => void;
}

type CellIndexesByRowByDateMap = Dictionary<Dictionary<number>>;

const nonApplicableFields = [NameField.name, TotalReportedField.name, ApprovalStatusField.name];

const _totalRow: IExtensibleEntity & IMaybeTotal = {
    id: "totalRow",
    attributes: {
        Name: "Total Reported"
    },
    isTotal: true
};

export const TimeTrackingPersistTimelineList = (props: TimeTrackingGridCoreProps) => {
    const {
        lines,
        projectLines,
        timeFrame,
        isReadonly,
        isPeriodOpenForReporting,
        isLoading,
        resourceCalendarDataSet,
        controlSettings,
        firstDayOfWeek,
        showApprovalColumn,
        showTotalReportedColumn = true,
        showLineMenu = !isReadonly,
        showCellTooltip = true,
        nameColumnLabel,
        additionalFields,
        onAdditionalFieldRender,
        onNameSuffixRender,
        inlineEditProps,
        isLineReadonly,
        cellValueTypes,
        getCellValue,
        onCellEditCompleted,
        onEditEntry,
        onAddTime,
        onCreateEntry,
        onDeleteEntries,
        onUpdateEntries,
        onAddTimeEntries,
        onClearTimeEntries,
        onCopyTask,
        onCloneEntry,
        onRecallLine
    } = props;

    const isReadonlyDueToPeriod = !isPeriodOpenForReporting;

    const isProjectTimeTrackingDisabled = (line: TimeTrackingLine) =>
        !!line.attributes.ProjectDetails && !line.attributes.ProjectDetails.allowTimeTracking;

    const isLineLocked =
        props.isLineLocked ??
        ((line: TimeTrackingLine) =>
            isReadonlyDueToPeriod ||
            IsTimesheetLineReadonly(line.attributes.TimesheetLine) ||
            isProjectTimeTrackingDisabled(line));

    const getLineReadonly = (line: TimeTrackingLine): TimeTrackingReadonly => ({
        global: isReadonly,
        approval: IsTimesheetLineReadonly(line.attributes.TimesheetLine),
        projectDisabled: isProjectTimeTrackingDisabled(line)
    });

    const cellByIndexMap = useRef<Dictionary<any>>({}).current;
    const cellPositionToIndexMap = useRef<Dictionary<number>>({}).current;
    const cellSelection = useRef<SelectionExt>(new SelectionExt()).current;
    const isCellEditingStarted = useRef(false);

    const [cellIndexMap, setCellIndexMap] = useState<CellIndexesByRowByDateMap>({});
    const [cellsInRow, setCellsInRow] = useState(0);
    const [totalCells, setTotalCells] = useState(0);

    const totalRow = useMemo(() => {
        const totalReported = lines.reduce((sum, current) => sum + current.attributes.TotalReported, 0);
        return {
            ..._totalRow,
            attributes: {
                ..._totalRow.attributes,
                TotalReported: totalReported
            }
        };
    }, [lines]);

    const fields = useMemo(
        () => [
            nameColumnLabel ? { ...NameField, label: nameColumnLabel } : NameField,
            ...(showTotalReportedColumn ? [TotalReportedField] : []),
            ...(showApprovalColumn ? [ApprovalStatusField] : []),
            ...(additionalFields ?? [])
        ],
        [showApprovalColumn, showTotalReportedColumn, nameColumnLabel, additionalFields]
    );

    const typedValuesOf = (segment: IScaleTimelineSegment, timeTrackingLines: TimeTrackingLine[]) =>
        cellValueTypes?.map(type => ({
            type,
            value: timeTrackingLines.reduce<number | undefined>((sum, line) => {
                const value = getCellValue?.(line, segment, type.key);
                return value === undefined ? sum : (sum ?? 0) + value;
            }, undefined)
        }));

    const displayFields = useMemo(() => fields.map(f => f.name), [fields]);

    const buildRow = useCallback((_: IExtensibleEntity) => BuildLineGroup(_, projectLines, lines), [lines, projectLines]);

    useEffect(() => {
        if (isLoading) {
            return;
        }

        const entityIds = lines.map(_ => _.id);
        const scale = TimelineScale.build(
            { start: timeFrame.start, end: timeFrame.end.getEndOfDay() },
            Quantization.days,
            firstDayOfWeek
        );
        const newCellIndexMap = _buildCellIndexMap(entityIds, scale.scale);

        cellSelection.setItems(newCellIndexMap.endToEndItems, false);

        const newCellsInRow =
            Object.keys(newCellIndexMap.map).length > 0
                ? Object.keys(newCellIndexMap.map[Object.keys(newCellIndexMap.map)[0]]).length
                : 0;

        clearCellMaps();
        setCellIndexMap(newCellIndexMap.map);
        setCellsInRow(newCellsInRow);
        setTotalCells(newCellIndexMap.endToEndItems.length);
    }, [lines, isLoading]);

    const clearCellMaps = () => {
        ClearObject(cellByIndexMap);
        ClearObject(cellPositionToIndexMap);
    };

    const selectCellByIndex = (idx: number, preserveSelection: boolean) => {
        const select = !cellSelection.isIndexSelected(idx);

        if (!preserveSelection) {
            cellSelection.getSelectedIndices().forEach(_ => {
                cellSelection.setIndexSelected(_, false, true);
            });
        }

        cellSelection.setIndexSelected(idx, select, true);
    };

    const navigateToCellByIndex = useCallback(
        (idx: number, preserveSelection: boolean) => {
            cellByIndexMap[idx]?.focus();
            selectCellByIndex(idx, preserveSelection);
        },
        [cellByIndexMap]
    );

    let cellPosition = 0;

    return (
        <PersistTimelineList
            key="timeline"
            inlineEditProps={inlineEditProps && { ...inlineEditProps, nonApplicableFields }}
            fields={fields}
            displayFields={displayFields}
            buildTree
            entityType={EntityType.TimeTrackingEntry}
            scaleMultiplier={1.5 + (cellValueTypes?.length ?? 1)}
            primaryOutlineLevel={1}
            checkboxVisibility={CheckboxVisibility.hidden}
            controlSettings={{
                ...controlSettings,
                timeline: {
                    ...controlSettings.timeline,
                    timeframe: timeFrame,
                    quantization: Quantization.days
                }
            }}
            initialTimeframe={timeFrame}
            scaleRenderMode={ScaleRenderMode.Cell}
            expandedEntitiesIds={[totalRow.id, ...projectLines.map(_ => _.attributes.Project.id)]}
            buildRow={buildRow}
            entities={[totalRow]}
            renderTimelineHeaderFirstRow={() => <></>}
            isDayOff={
                resourceCalendarDataSet
                    ? (date: Date) => getWorkingHoursBetweenDates(date, date, resourceCalendarDataSet) === 0
                    : undefined
            }
            onItemRender={(
                timeTrackingLine: TimeTrackingLine & IMaybeTotal & WithEntityType,
                index: number,
                field: Field,
                defaultRender: any
            ) => {
                if (field.name === NameField.name) {
                    return (
                        <div
                            className={
                                "item-name " +
                                (timeTrackingLine.attributes.SuggestionType ? "suggestion " : "") +
                                timeTrackingLine.entityType?.toString().toLowerCase()
                            }>
                            <TimeTrackingLineEntityName
                                timeTrackingLine={timeTrackingLine}
                                defaultRender={defaultRender}
                                field={field}
                            />
                            {onNameSuffixRender?.(timeTrackingLine)}
                        </div>
                    );
                }

                if (field.name === TotalReportedField.name) {
                    return (
                        <div className="total-reported">
                            {formatValue(timeTrackingLine.attributes.TotalReported, FormatType.Duration)}
                        </div>
                    );
                }

                if (field.name === ApprovalStatusField.name) {
                    const timesheetLine = timeTrackingLine.attributes.TimesheetLine;
                    if (!timesheetLine) {
                        return <div className="approval-status"></div>;
                    }

                    return (
                        <div className="approval-status">
                            <TooltipHost
                                tooltipProps={{
                                    onRenderContent: () => <ApprovalStatusTooltip timeline={timeTrackingLine} />,
                                    maxWidth: "450px"
                                }}
                                delay={TooltipDelay.long}>
                                {renderApprovalStatusIcon(timesheetLine.status)}
                            </TooltipHost>
                        </div>
                    );
                }

                return onAdditionalFieldRender?.(timeTrackingLine, index, field, defaultRender) ?? defaultRender();
            }}
            onItemMenuRender={
                showLineMenu
                    ? (item: IExtensibleEntity & WithEntityType & TimeTrackingLine) => {
                          return (
                              <TimeTrackingItemMenu
                                  entityType={item.entityType}
                                  timeTrackingLine={item}
                                  readonly={getLineReadonly(item)}
                                  onClearTimeEntries={toClear => {
                                      onClearTimeEntries?.(toClear);
                                  }}
                                  onDeleteTimeEntries={entryIds => {
                                      onDeleteEntries?.(entryIds);
                                  }}
                                  onAddTime={type => {
                                      if (item.attributes.ProjectDetails) {
                                          onAddTime?.(item.attributes.ProjectDetails, type);
                                      }
                                  }}
                                  onCopyTask={toCopy => {
                                      onCopyTask?.(toCopy);
                                  }}
                                  onRecallLine={onRecallLine}
                              />
                          );
                      }
                    : undefined
            }
            renderSegmentContent={(row: IRow, segment: IScaleTimelineSegment) => {
                const expected = resourceCalendarDataSet
                    ? getWorkingHoursBetweenDates(segment.startDate, segment.finishDate, resourceCalendarDataSet)
                    : 0;

                const isDayOff = expected === 0;

                if (row.subItemType === EntityType.Task) {
                    if (cellPosition >= totalCells) {
                        cellPosition = 0;
                    }

                    const timeTrackingLine = row.entity as TimeTrackingLine;
                    const idx = cellIndexMap[row.key]?.[segment.startDate.getTime()];
                    const position = cellPosition;

                    if (isPeriodOpenForReporting) {
                        cellPositionToIndexMap[position] = idx;
                    }

                    cellPosition++;

                    return (
                        <TimeTrackingCell
                            timeTrackingLine={timeTrackingLine}
                            segment={segment}
                            selection={cellSelection}
                            selectionIndex={idx}
                            isLocked={isLineLocked(timeTrackingLine)}
                            isReadonly={isReadonly || !!isLineReadonly?.(timeTrackingLine)}
                            isDayOff={isDayOff}
                            typedValues={typedValuesOf(segment, [timeTrackingLine])}
                            onTypedValueEditCompleted={(typeKey, value) => {
                                onCellEditCompleted?.(timeTrackingLine, segment, typeKey, value);
                                isCellEditingStarted.current = false;
                            }}
                            onEditStarted={() => {
                                isCellEditingStarted.current = true;
                            }}
                            onEditCompleted={updates => {
                                if (isReadonly) {
                                    return;
                                }

                                updates.forEach(_ => {
                                    if (_.id) {
                                        onUpdateEntries?.([
                                            {
                                                id: _.id,
                                                attributes: {
                                                    [nameof<TimeTrackingEntryAttr>("Duration")]: _.attributes.Duration
                                                }
                                            }
                                        ]);
                                    } else if (_.attributes.Duration) {
                                        const sourceInfo: TimeTrackingEntrySourceInfo | undefined =
                                            timeTrackingLine.attributes.SuggestionType ===
                                            TimeTrackingSuggestionType.ResourcePlan
                                                ? {
                                                      sourceId: _.attributes.Project!.id,
                                                      sourceType: "ResourcePlan",
                                                      type: TimeTrackingEntrySourceType.ResourcePlaSuggestion
                                                  }
                                                : undefined;

                                        onAddTimeEntries?.([
                                            {
                                                attributes: _.attributes,
                                                sourceInfo: sourceInfo
                                            }
                                        ]);
                                    }
                                });

                                isCellEditingStarted.current = false;
                            }}
                            componentRef={_ => {
                                if (idx !== undefined && _) {
                                    cellByIndexMap[idx] = _;
                                }
                            }}
                            onClick={
                                idx !== undefined && isPeriodOpenForReporting
                                    ? e => {
                                          e.stopPropagation();
                                          const preserveSelection = false;
                                          navigateToCellByIndex(idx, preserveSelection);
                                      }
                                    : undefined
                            }
                            onKeyDown={
                                isPeriodOpenForReporting
                                    ? e => {
                                          let nextPosition = undefined;
                                          if (e.key === "ArrowLeft") {
                                              nextPosition = position - 1;
                                          } else if (e.key === "ArrowRight" || e.key === "Tab" || e.key === "Enter") {
                                              nextPosition = position + 1;
                                          } else if (e.key === "ArrowUp") {
                                              nextPosition = position - cellsInRow;
                                          } else if (e.key === "ArrowDown") {
                                              nextPosition = position + cellsInRow;
                                          }

                                          if (
                                              nextPosition !== undefined &&
                                              nextPosition < totalCells &&
                                              cellPositionToIndexMap[nextPosition] !== undefined
                                          ) {
                                              navigateToCellByIndex(cellPositionToIndexMap[nextPosition]!, false);
                                              e.preventDefault();
                                          }
                                      }
                                    : undefined
                            }
                        />
                    );
                } else {
                    return (
                        <TotalTimeTrackingCell
                            row={row}
                            segment={segment}
                            isDayOff={isDayOff}
                            expected={row.entity.id === totalRow.id ? expected : undefined}
                            typedValues={typedValuesOf(segment, getTimeTrackinLines(row))}
                        />
                    );
                }
            }}
            renderSegmentTooltipContent={(row, segment) => {
                if (!showCellTooltip || row.subItemType !== EntityType.Task || isCellEditingStarted.current) {
                    return undefined;
                }

                const timeTrackingLine = getTimeTrackinLines(row)[0]!;

                return (
                    <TimeTrackingCellTooltip
                        timeTrackingLine={timeTrackingLine}
                        segment={segment}
                        readonly={getLineReadonly(timeTrackingLine)}
                        resourceCalendarDataSet={resourceCalendarDataSet!}
                        onClone={toClone => onCloneEntry?.(toClone)}
                        onStartEditing={entry => {
                            const timeType = entry.attributes.AdministrativeCategory
                                ? TimeType.Administrative
                                : entry.attributes.CustomTimeEntryName
                                  ? TimeType.CustomEvent
                                  : TimeType.ProjectTask;

                            onEditEntry?.(entry, timeType);
                        }}
                        onCreate={() => {
                            onCreateEntry?.(segment as IScaleTimelineSegment, timeTrackingLine);
                        }}
                        onEditCompleted={(id, attributes) => {
                            onUpdateEntries?.([{ id, attributes }]);
                        }}
                    />
                );
            }}
        />
    );
};

const _buildCellIndexMap = (entitiesIds: string[], scale: any): { map: CellIndexesByRowByDateMap; endToEndItems: any[] } => {
    return entitiesIds
        .map((entityId, rowIndex) => ({
            entityId,
            datesWithIndex: scale.dates.map((_: any, index: number) => ({
                date: _,
                endToEndIndex: rowIndex * scale.dates.length + index
            }))
        }))
        .reduce<{ map: CellIndexesByRowByDateMap; endToEndItems: any[] }>(
            (prev, cur) => ({
                map: {
                    ...prev.map,
                    [cur.entityId]: cur.datesWithIndex.reduce(
                        (_prev: any, _cur: any) => ({
                            ..._prev,
                            [_cur.date.start.getTime()]: _cur.endToEndIndex
                        }),
                        {}
                    )
                },
                endToEndItems: [
                    ...prev.endToEndItems,
                    ...cur.datesWithIndex.map((_: any) => ({
                        key: _.endToEndIndex,
                        entityId: cur.entityId,
                        start: _.date.start,
                        finish: _.date.finish
                    }))
                ]
            }),
            { map: {}, endToEndItems: [] }
        );
};

